import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Play, RotateCcw, Trophy, ArrowDown } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const CANDIDATES = [
  { id: "alp", name: "Alex Chen", party: "Labor (ALP)", color: "#CC2529", abbr: "ALP" },
  { id: "lib", name: "Sarah Mitchell", party: "Liberal Party", color: "#1B4F9E", abbr: "LIB" },
  { id: "grn", name: "Jordan Rivers", party: "The Greens", color: "#10B981", abbr: "GRN" },
  { id: "nat", name: "Robert Hayes", party: "Nationals", color: "#155E3B", abbr: "NAT" },
  { id: "tea", name: "Emily Tran", party: "Teal Independent", color: "#0D9488", abbr: "TEA" },
  { id: "on", name: "Bruce Walker", party: "One Nation", color: "#D97706", abbr: "ON" },
];

const DIST = [
  { primary: "alp", rest: ["grn", "tea", "lib", "nat", "on"], pct: 34 },
  { primary: "lib", rest: ["nat", "tea", "alp", "on", "grn"], pct: 30 },
  { primary: "grn", rest: ["alp", "tea", "lib", "nat", "on"], pct: 14 },
  { primary: "on", rest: ["lib", "nat", "alp", "tea", "grn"], pct: 8 },
  { primary: "tea", rest: ["grn", "alp", "lib", "nat", "on"], pct: 7 },
  { primary: "nat", rest: ["lib", "tea", "alp", "on", "grn"], pct: 7 },
];

function generateElectorate(userBallot) {
  const voters = [userBallot];
  for (let i = 1; i < 1000; i++) {
    const r = Math.random() * 100;
    let cum = 0;
    for (const d of DIST) {
      cum += d.pct;
      if (r < cum) {
        const rest = [...d.rest];
        if (Math.random() < 0.35 && rest.length >= 2) {
          const idx = Math.floor(Math.random() * (rest.length - 1));
          [rest[idx], rest[idx + 1]] = [rest[idx + 1], rest[idx]];
        }
        voters.push([d.primary, ...rest]);
        break;
      }
    }
  }
  return voters;
}

function runIRV(voters) {
  let remaining = CANDIDATES.map((c) => c.id);
  const rounds = [];
  while (remaining.length > 1) {
    const counts = {};
    remaining.forEach((id) => (counts[id] = 0));
    let total = 0;
    for (const ballot of voters) {
      const first = ballot.find((id) => remaining.includes(id));
      if (first) { counts[first]++; total++; }
    }
    const pcts = {};
    remaining.forEach((id) => (pcts[id] = Math.round((counts[id] / total) * 100)));
    const winner = remaining.find((id) => counts[id] / total > 0.5);
    if (winner) {
      rounds.push({ counts, pcts, total, winner, eliminated: null });
      break;
    }
    const minVotes = Math.min(...remaining.map((id) => counts[id]));
    const eliminated = remaining.find((id) => counts[id] === minVotes);
    rounds.push({ counts, pcts, total, winner: null, eliminated });
    remaining = remaining.filter((id) => id !== eliminated);
  }
  if (remaining.length === 1 && !rounds[rounds.length - 1].winner) {
    rounds[rounds.length - 1].winner = remaining[0];
  }
  return rounds;
}

function getCandidateById(id) {
  return CANDIDATES.find((c) => c.id === id);
}

export default function VotingSimulatorPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [preferences, setPreferences] = useState({});
  const [rounds, setRounds] = useState([]);
  const [ran, setRan] = useState(false);

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";

  const prefCount = Object.keys(preferences).length;

  const assignPref = (candidateId) => {
    if (preferences[candidateId]) return;
    const next = prefCount + 1;
    setPreferences((prev) => ({ ...prev, [candidateId]: next }));
    setRan(false);
    setRounds([]);
  };

  const removePref = (candidateId) => {
    const removed = preferences[candidateId];
    const updated = {};
    for (const [id, rank] of Object.entries(preferences)) {
      if (id === candidateId) continue;
      updated[id] = rank > removed ? rank - 1 : rank;
    }
    setPreferences(updated);
    setRan(false);
    setRounds([]);
  };

  const reset = () => {
    setPreferences({});
    setRounds([]);
    setRan(false);
  };

  const runSimulation = () => {
    const ordered = Object.entries(preferences)
      .sort((a, b) => a[1] - b[1])
      .map(([id]) => id);
    const unranked = CANDIDATES.filter((c) => !preferences[c.id]).map((c) => c.id);
    const ballot = [...ordered, ...unranked];
    const electorate = generateElectorate(ballot);
    const result = runIRV(electorate);
    setRounds(result);
    setRan(true);
  };

  const winner = ran && rounds.length ? getCandidateById(rounds[rounds.length - 1].winner) : null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="simulator-page">
      {/* Hero */}
      <div className="py-12 px-4 border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-sm font-bold mb-5 hover:opacity-75"
            style={{ color: "#FFC82E" }}
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </button>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Voting Simulator
          </h1>
          <p className="text-sm max-w-xl" style={{ color: "#94A3B8" }}>
            See how Australia's preferential voting works. Rank candidates in order of preference,
            then run the simulation to watch preferences flow round-by-round across 1,000 voters.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Explainer */}
        <div className="border-2 border-black p-4 mb-8" style={{ backgroundColor: "#FFC82E25", borderLeft: "4px solid #FFC82E" }}>
          <p className="text-sm font-bold" style={{ color: text }}>
            How it works: Click candidates in order of your preference (1st choice, 2nd, etc.).
            Unranked candidates are automatically assigned last preferences.
            The simulator runs Instant-Runoff Voting across a sample electorate of 1,000 voters.
          </p>
        </div>

        {/* Candidates */}
        <h2 className="font-black text-lg mb-4" style={{ fontFamily: "Outfit, sans-serif", color: text }}>
          Step 1 — Rank your preferences
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
          {CANDIDATES.map((c) => {
            const rank = preferences[c.id];
            return (
              <button
                key={c.id}
                data-testid={`candidate-${c.id}`}
                onClick={() => (rank ? removePref(c.id) : assignPref(c.id))}
                className="relative border-2 border-black p-4 text-left transition-all hover:-translate-y-0.5"
                style={{
                  backgroundColor: rank ? c.color : surface,
                  boxShadow: rank ? "4px 4px 0 #000" : "2px 2px 0 #000",
                  opacity: ran ? 0.8 : 1,
                }}
              >
                {rank && (
                  <div className="absolute top-2 right-2 w-7 h-7 border-2 border-black flex items-center justify-center font-black text-sm bg-white text-black">
                    {rank}
                  </div>
                )}
                <p className="font-black text-sm mb-0.5" style={{ color: rank ? "#fff" : text }}>
                  {c.name}
                </p>
                <p className="text-xs font-bold" style={{ color: rank ? "rgba(255,255,255,0.75)" : textSec }}>
                  {c.party}
                </p>
                {!rank && (
                  <p className="text-xs mt-1 font-medium" style={{ color: "#10B981" }}>
                    Click to rank
                  </p>
                )}
                {rank && (
                  <p className="text-xs mt-1 font-medium text-white opacity-75">
                    Click to remove
                  </p>
                )}
              </button>
            );
          })}
        </div>

        {/* Controls */}
        <div className="flex gap-3 mb-10">
          <button
            onClick={runSimulation}
            data-testid="run-simulation-btn"
            disabled={prefCount === 0}
            className="flex items-center gap-2 px-6 py-3 font-black text-sm border-2 border-black hover:-translate-y-0.5 transition-transform disabled:opacity-50"
            style={{ backgroundColor: "#FFC82E", boxShadow: "3px 3px 0 #000" }}
          >
            <Play className="w-4 h-4" /> Run Election ({prefCount || 0} ranked)
          </button>
          <button
            onClick={reset}
            className="flex items-center gap-2 px-4 py-3 font-bold text-sm border-2 border-black hover:-translate-y-0.5 transition-transform"
            style={{ backgroundColor: surface, color: text, boxShadow: "2px 2px 0 #000" }}
          >
            <RotateCcw className="w-4 h-4" /> Reset
          </button>
        </div>

        {/* Results */}
        {ran && rounds.length > 0 && (
          <div data-testid="simulation-results">
            <h2 className="font-black text-lg mb-1" style={{ fontFamily: "Outfit, sans-serif", color: text }}>
              Step 2 — Count Results
            </h2>
            <p className="text-xs mb-6" style={{ color: textSec }}>
              Based on 1,000 simulated voters with realistic voting patterns.
            </p>

            {rounds.map((round, ri) => (
              <div key={ri} className="mb-6">
                <div
                  className="border-2 border-black p-2 mb-3 flex items-center justify-between"
                  style={{ backgroundColor: "#0F172A" }}
                >
                  <span className="font-black text-white text-sm" style={{ fontFamily: "Outfit" }}>
                    Round {ri + 1}
                  </span>
                  {round.eliminated && (
                    <span className="text-xs font-bold px-2 py-0.5 border border-red-500 text-red-400">
                      Eliminated: {getCandidateById(round.eliminated)?.abbr}
                    </span>
                  )}
                  {round.winner && (
                    <span className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 border border-yellow-400 text-yellow-400">
                      <Trophy className="w-3 h-3" /> Winner!
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  {CANDIDATES.filter((c) => round.counts[c.id] !== undefined)
                    .sort((a, b) => round.counts[b.id] - round.counts[a.id])
                    .map((c) => {
                      const pct = round.pcts[c.id] || 0;
                      const isElim = round.eliminated === c.id;
                      const isWin = round.winner === c.id;
                      return (
                        <div key={c.id} className="flex items-center gap-3">
                          <span
                            className="text-xs font-black w-10 flex-shrink-0 text-center px-1 py-0.5 border border-black"
                            style={{ backgroundColor: c.color, color: "#fff" }}
                          >
                            {c.abbr}
                          </span>
                          <div className="flex-1 border border-black bg-transparent h-7 relative overflow-hidden">
                            <div
                              className="h-full transition-all duration-500"
                              style={{
                                width: `${pct}%`,
                                backgroundColor: isElim ? "#6B7280" : isWin ? c.color : c.color + "CC",
                              }}
                            />
                            <span
                              className="absolute inset-0 flex items-center px-2 text-xs font-black"
                              style={{ color: pct > 20 ? "#fff" : text }}
                            >
                              {round.counts[c.id]} votes ({pct}%)
                            </span>
                          </div>
                          {isElim && <span className="text-xs font-bold text-red-500 w-6">✕</span>}
                          {isWin && <Trophy className="w-4 h-4 flex-shrink-0" style={{ color: "#FFC82E" }} />}
                        </div>
                      );
                    })}
                </div>
                {ri < rounds.length - 1 && (
                  <div className="flex items-center justify-center my-4">
                    <ArrowDown className="w-5 h-5" style={{ color: textSec }} />
                    <span className="ml-2 text-xs font-bold" style={{ color: textSec }}>
                      Redistributing {getCandidateById(round.eliminated)?.abbr} preferences...
                    </span>
                  </div>
                )}
              </div>
            ))}

            {winner && (
              <div
                className="border-4 border-black p-6 text-center mt-6"
                style={{ backgroundColor: winner.color, boxShadow: "6px 6px 0 #000" }}
                data-testid="winner-display"
              >
                <Trophy className="w-8 h-8 text-white mx-auto mb-2" />
                <p className="text-xl font-black text-white mb-1" style={{ fontFamily: "Outfit" }}>
                  {winner.name} wins!
                </p>
                <p className="text-sm text-white font-bold opacity-90">{winner.party}</p>
                <p className="text-xs text-white opacity-75 mt-2">
                  After {rounds.length} round{rounds.length > 1 ? "s" : ""} of counting
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
